/**
 * Utility for formatting customer names consistently across the Conversations module.
 * Replaces ugly prefixes like `client:`, numeric Facebook PSIDs, and legacy placeholders
 * with real account holder names or clean fallbacks.
 */

const LEGACY_NAME_PREFIX = /^(?:Email|Phone|SMS|WhatsApp)?\s*Customer\s*-\s*(?:[a-z]+\s*-\s*)?/i
const CHANNEL_PREFIX = /^(?:client:|facebook:|internal:|wa:)/i

/**
 * Strips technical prefixes (such as 'client:', 'facebook:') and legacy customer placeholders.
 */
export function cleanIdentifier(value?: string | null): string {
  if (!value) return ''
  const trimmed = value.trim()
  const strippedLegacy = trimmed.replace(LEGACY_NAME_PREFIX, '').trim()
  return strippedLegacy.replace(CHANNEL_PREFIX, '').trim()
}

/**
 * Checks whether a string is a raw numeric ID (e.g. Facebook PSID '38374932685439521') or a UUID.
 */
export function isNumericOrRawId(value?: string | null): boolean {
  if (!value) return false
  const clean = cleanIdentifier(value)
  if (!clean) return false
  return (
    /^\d{5,}$/.test(clean) ||
    // Placeholder handle the Instagram poller gives a commenter Graph didn't
    // identify — it's a comment id, not a person.
    /^ig_commenter_\d+$/.test(clean) ||
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(clean)
  )
}

/**
 * Formats a customer / account holder name for user-facing UI.
 * - Strips 'client:' and raw numeric IDs.
 * - Prioritizes real Facebook account holder name or contact name.
 * - For Facebook channel, falls back to 'Facebook User' instead of exposing raw numeric IDs.
 * - For other channels, falls back to cleaned handle, or 'Customer' / 'Unknown'.
 */
export function formatCustomerName(
  displayName?: string | null,
  primaryHandle?: string | null,
  channelOrigin?: string | null,
  fbAccountHolderName?: string | null,
): string {
  const isFb = (channelOrigin ?? '').toLowerCase() === 'facebook'

  // 1. Explicit Facebook account holder name (from post/comment/profile)
  if (isFb && fbAccountHolderName && fbAccountHolderName.trim()) {
    const cleanFb = cleanIdentifier(fbAccountHolderName)
    if (cleanFb && !isNumericOrRawId(cleanFb)) {
      return cleanFb
    }
  }

  // 2. Resolved display name (if not a raw numeric ID or prefixed ID)
  const cleanName = cleanIdentifier(displayName)
  if (cleanName && !isNumericOrRawId(cleanName)) {
    return cleanName
  }

  // 3. Primary handle (if human-readable like email or phone, but not raw Facebook numeric ID)
  const cleanHandle = cleanIdentifier(primaryHandle)
  if (cleanHandle && !isNumericOrRawId(cleanHandle)) {
    return cleanHandle
  }

  // 4. Fallback for Facebook vs other channels
  if (isFb) {
    return 'Facebook User'
  }

  return cleanName || cleanHandle || 'Customer'
}

/**
 * Derives 1 or 2 uppercase initials for avatars.
 */
export function getCustomerInitials(name?: string | null): string {
  if (!name) return '?'
  const clean = cleanIdentifier(name)
  if (!clean || clean === '?') return '?'
  const words = clean.split(/\s+/).filter(Boolean)
  if (words.length === 0) return '?'
  if (words.length === 1) {
    return words[0]!.slice(0, 2).toUpperCase()
  }
  return ((words[0]![0] ?? '') + (words[1]![0] ?? '')).toUpperCase()
}
